/**
 * Feb 2026 · Forms admin list page.
 *
 * Lists every form in the workspace with a preview of URL, submission
 * counts (30d), and quick actions (edit, duplicate, delete, view
 * public link).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Plus, ExternalLink, Copy, Trash2, Pencil, FileText, CheckCircle2, Circle, Inbox } from "lucide-react";

export default function FormsPage() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [creatingTpl, setCreatingTpl] = useState(null);

  useEffect(() => {
    api.get("/form-templates").then(({ data }) => setTemplates(data?.templates || [])).catch(() => {});
  }, []);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/forms`);
      setRows(data?.forms || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const create = async () => {
    if (!newTitle.trim()) { toast.error("Give the form a title"); return; }
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/forms`, { title: newTitle.trim() });
      setNewTitle("");
      toast.success("Form created");
      nav(`/app/forms/${data.form.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  const createFromTemplate = async (tpl) => {
    setCreatingTpl(tpl.id);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/forms/from-template`, { template_id: tpl.id });
      toast.success(`Created "${data.form.title}"`);
      nav(`/app/forms/${data.form.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create from template failed")); }
    finally { setCreatingTpl(null); }
  };

  const duplicate = async (fid) => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/forms/${fid}/duplicate`);
      toast.success("Duplicated");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Duplicate failed")); }
  };

  const del = async (fid) => {
    if (!window.confirm("Archive this form? Existing submissions are kept.")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/forms/${fid}`);
      toast.success("Archived");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Archive failed")); }
  };

  const publicUrl = (fid) => `${window.location.origin}/f/${fid}`;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-8" data-testid="forms-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy">Forms</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Build lead-capture forms. Publish as a hosted URL, embed anywhere, or drop into WordPress.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New form title…"
          className="h-10 border border-border px-3 flex-1 min-w-[240px] rounded-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="forms-new-title"
        />
        <Button onClick={create} className="h-10 rounded-none" data-testid="forms-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Create form
        </Button>
        <Button variant="outline" className="h-10 rounded-none" onClick={() => setShowTemplates((v) => !v)} data-testid="forms-templates-toggle">
          <FileText className="w-4 h-4 mr-2" /> {showTemplates ? "Hide templates" : "Start from template"}
        </Button>
      </div>

      {/* Feb 2026 · Template gallery — one-click ready-made forms. */}
      {showTemplates && templates.length > 0 && (
        <div className="mb-6 border border-border bg-muted/20 p-4" data-testid="forms-templates-gallery">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">Ready-made templates · {templates.length}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => createFromTemplate(t)}
                disabled={creatingTpl === t.id}
                className="text-left border border-border bg-background hover:border-primary hover:bg-primary/5 transition-colors p-3 rounded-none disabled:opacity-50"
                data-testid={`forms-template-${t.id}`}
              >
                <div className="text-2xl mb-2 leading-none">{t.icon}</div>
                <div className="font-semibold text-sm mb-1 truncate">{t.name}</div>
                <div className="text-[11px] text-muted-foreground line-clamp-2">{t.description}</div>
                <div className="text-[10px] text-primary mt-2">{creatingTpl === t.id ? "Creating…" : "Use this template →"}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border" data-testid="forms-list">
        <div className="hidden sm:grid grid-cols-[minmax(0,3fr),minmax(0,2fr),80px,80px,90px,90px,180px] bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground border-b border-border">
          <div className="px-3 py-2">Form</div>
          <div className="px-3 py-2">Public URL</div>
          <div className="px-3 py-2 text-right" title="Public views · last 30 days">Views 30d</div>
          <div className="px-3 py-2 text-right" title="Submissions · last 30 days">Subs 30d</div>
          <div className="px-3 py-2 text-right" title="Conversion = submissions ÷ views">Conv %</div>
          <div className="px-3 py-2 text-center">Status</div>
          <div className="px-3 py-2 text-right">Actions</div>
        </div>
        {rows.length === 0 && (
          <div className="p-10 text-center text-muted-foreground text-sm">
            {busy ? "Loading…" : "No forms yet — create your first one above."}
          </div>
        )}
        {rows.map((f) => {
          const conv = Number(f.conversion_30d || 0);
          // Feb 2026 · Conversion colour bands — reds < 5%, amber < 15%,
          // emerald ≥ 15%. Fine-tune later if agencies push back on
          // the thresholds; this matches typical B2B lead-gen norms.
          const convColor = conv >= 15
            ? "text-emerald-700"
            : conv >= 5 ? "text-amber-700" : "text-rose-700";
          return (
          <div key={f.id}
            className="grid grid-cols-1 sm:grid-cols-[minmax(0,3fr),minmax(0,2fr),80px,80px,90px,90px,180px] sm:items-center border-b border-border last:border-b-0 hover:bg-muted/20 text-sm"
            data-testid={`forms-row-${f.id}`}
          >
            <div className="px-3 py-3 min-w-0">
              <div className="font-semibold truncate">{f.title}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                {(f.fields || []).length} fields · updated {String(f.updated_at || "").slice(0, 10)}
              </div>
              <div className="sm:hidden flex items-center gap-3 mt-1.5 text-[11px]">
                {f.published ? (
                  <span className="text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Live
                  </span>
                ) : (
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Circle className="w-3 h-3" /> Draft
                  </span>
                )}
                <span className="text-muted-foreground">
                  {f.submissions_30d ?? 0} subs · {f.views_30d ?? 0} views · <span className={convColor}>{conv}%</span>
                </span>
              </div>
            </div>
            <div className="px-3 py-1 sm:py-3 min-w-0">
              <div className="flex items-center gap-1.5">
                <code className="text-[11px] text-muted-foreground truncate flex-1 min-w-0">/f/{f.slug || f.id}</code>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-primary"
                  onClick={() => { navigator.clipboard.writeText(publicUrl(f.slug || f.id)); toast.success("Public URL copied"); }}
                  title="Copy public URL"
                  data-testid={`forms-row-copy-${f.id}`}
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                {f.published && (
                  <a href={publicUrl(f.slug || f.id)} target="_blank" rel="noreferrer"
                    className="text-muted-foreground hover:text-primary" title="Open">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
            <div className="hidden sm:block px-3 py-3 text-right font-mono text-muted-foreground"
                  data-testid={`forms-row-views-${f.id}`}>
              {f.views_30d ?? 0}
            </div>
            <div className="hidden sm:block px-3 py-3 text-right font-mono"
                  data-testid={`forms-row-subs-${f.id}`}>
              {f.submissions_30d ?? 0}
            </div>
            <div className={`hidden sm:block px-3 py-3 text-right font-mono font-semibold ${convColor}`}
                  data-testid={`forms-row-conv-${f.id}`}
                  title={(f.views_30d ?? 0) === 0 && (f.submissions_30d ?? 0) === 0 ? "No traffic yet" : `${f.submissions_30d ?? 0} of ${f.views_30d ?? 0} visitors`}>
              {(f.views_30d ?? 0) === 0 && (f.submissions_30d ?? 0) === 0 ? "—" : `${conv}%`}
            </div>
            <div className="hidden sm:block px-3 py-3 text-center">
              {f.published ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold" data-testid={`forms-row-status-${f.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Circle className="w-3.5 h-3.5" /> Draft
                </span>
              )}
            </div>
            <div className="px-3 pb-3 sm:py-3 sm:text-right space-x-1">
              <Button size="sm" variant="outline" className="rounded-none h-8"
                onClick={() => nav(`/app/forms/${f.id}`)}
                data-testid={`forms-row-edit-${f.id}`}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-8"
                onClick={() => nav(`/app/forms/${f.id}?tab=submissions`)}
                data-testid={`forms-row-entries-${f.id}`}
                title="View entries">
                <Inbox className="w-3 h-3 mr-1" /> Entries
              </Button>
              <Button size="sm" variant="ghost" className="rounded-none h-8"
                onClick={() => duplicate(f.id)}
                data-testid={`forms-row-dup-${f.id}`}
                title="Duplicate">
                <FileText className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="rounded-none h-8 text-rose-700 hover:bg-rose-50"
                onClick={() => del(f.id)}
                data-testid={`forms-row-del-${f.id}`}
                title="Archive">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
